import React, { useState } from 'react';
import { writingReviewAPI } from './api';
import './WritingReview.css';

export const MAX_REVIEW_CHARACTERS = 4000;

const CATEGORY_LABELS = {
  grammar: 'Grammar',
  clarity: 'Clarity',
  repetition: 'Repetition',
  run_on: 'Run-on phrasing'
};

export default function WritingReview() {
  const [text, setText] = useState('');
  const [findings, setFindings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = text.trim().length > 0
    && text.length <= MAX_REVIEW_CHARACTERS
    && !loading;

  const updateText = (event) => {
    setText(event.target.value);
    setFindings(null);
    setError('');
  };

  const submitReview = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setError('');
    setFindings(null);
    try {
      const response = await writingReviewAPI.review(text, 'en-CA');
      setFindings(response.data.findings);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error?.message
          || 'Writing review is currently unavailable.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="gpj-section gpj-writing-review" aria-labelledby="writing-review-title">
      <div className="gpj-wrap gpj-writing-review__wrap">
        <div className="gpj-sec-head">
          <span className="gpj-eyebrow">Writing mechanics</span>
          <h2 id="writing-review-title">Writing Review</h2>
          <p>Grammar and clarity assistance only.</p>
        </div>

        <aside className="gpj-writing-review__boundary">
          <strong>What this tool does</strong>
          <p>
            This tool reviews writing mechanics. It does not provide legal advice,
            decide what you should say, add facts, or assess your case.
          </p>
        </aside>

        <form className="gpj-writing-review__form" onSubmit={submitReview}>
          <label htmlFor="writing-review-text">Text to review</label>
          <textarea
            id="writing-review-text"
            value={text}
            onChange={updateText}
            maxLength={MAX_REVIEW_CHARACTERS}
            rows={10}
            placeholder="Paste or enter a short passage for grammar and clarity review."
          />
          <div className="gpj-writing-review__controls">
            <span className="gpj-writing-review__counter" aria-live="polite">
              {text.length.toLocaleString()} / {MAX_REVIEW_CHARACTERS.toLocaleString()} characters
            </span>
            <button
              type="submit"
              className="gpj-btn gpj-btn--primary gpj-btn--lg"
              disabled={!canSubmit}
            >
              {loading ? 'Reviewing…' : 'Review writing'}
            </button>
          </div>
        </form>

        {error && (
          <div className="gpj-error gpj-writing-review__message" role="alert">
            {error}
          </div>
        )}

        {findings !== null && (
          <section className="gpj-writing-review__results" aria-live="polite">
            <h3>Review findings</h3>
            {findings.length === 0 ? (
              <p className="gpj-writing-review__empty">
                No grammar or clarity findings were identified.
              </p>
            ) : (
              <div className="gpj-writing-review__finding-list">
                {findings.map((finding, index) => (
                  <article
                    className="gpj-writing-review__finding"
                    key={`${finding.category}-${finding.original}-${index}`}
                  >
                    <span className="gpj-writing-review__category">
                      {CATEGORY_LABELS[finding.category] || 'Writing mechanics'}
                    </span>
                    <dl>
                      <div>
                        <dt>Original</dt>
                        <dd>{finding.original}</dd>
                      </div>
                      <div>
                        <dt>Suggestion</dt>
                        <dd>{finding.suggestion}</dd>
                      </div>
                      <div>
                        <dt>Explanation</dt>
                        <dd>{finding.explanation}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </section>
  );
}
